const PLATFORM_ID_KEYS = ['id', 'platform_id'];
const PLATFORM_NAME_KEYS = ['name', 'platform_name'];
const ASSIGNMENT_ID_KEYS = ['id', 'assignment_id'];
const ASSIGNMENT_PLATFORM_NAME_KEYS = ['platform_name', 'platform'];
const ASSIGNMENT_PRINCIPAL_NAME_KEYS = ['principal_name', 'user_name', 'role_name'];
const ASSIGNMENT_ACCESS_LEVEL_KEYS = ['access_level', 'access'];
const SUCCESS_BADGE_STATUSES = Object.freeze(['Ready', 'Passed', 'Assigned', 'Connected']);
const WARNING_BADGE_STATUSES = Object.freeze(['Needs Review', 'Awaiting Retry', 'Pending']);
const FAILED_READINESS_RESULT = Object.freeze({
  health_endpoint_status: 'Failed',
  upsert_token_endpoint_status: 'Failed',
  sso_assert_status: 'Failed',
  reachable_status: 'Failed',
});

function get_value(source, key, fallback_value = null) {
  const value = Reflect.get(source, key);
  return value ?? fallback_value;
}

function get_first_available_value(source, keys, fallback_value = null) {
  for (const key of keys) {
    const value = Reflect.get(source, key);
    if (value !== undefined && value !== null && value !== '') return value;
  }

  return fallback_value;
}

export function clone_default_readiness() {
  return {
    selected_platform_name: 'No configuration selected',
    environment: 'Not selected',
    health_endpoint_status: 'Pending',
    upsert_token_endpoint_status: 'Pending',
    sso_assert_status: 'Pending',
    reachable_status: 'Pending',
    last_checked_at: '',
  };
}

export function create_default_checklist_value(masked_value = 'Not available') {
  return {
    masked_value,
    one_time_value: '',
  };
}

function sanitize_checklist_value(checklist_value, fallback_masked_value = 'Not available') {
  return {
    masked_value: checklist_value?.masked_value || fallback_masked_value,
    one_time_value: '',
  };
}

export function get_internal_api_key_checklist_value(checklist) {
  return sanitize_checklist_value(
    get_first_available_value(
      checklist,
      ['INTERNAL_API_KEY', 'internal_api_key'],
      create_default_checklist_value(),
    ),
  );
}

export function get_sso_secret_checklist_value(checklist) {
  return sanitize_checklist_value(
    get_first_available_value(
      checklist,
      ['SSO_SHARED_SECRET', 'sso_shared_secret'],
      create_default_checklist_value(),
    ),
  );
}

export function get_max_allowed_skew_checklist_value(checklist) {
  return sanitize_checklist_value(
    get_first_available_value(
      checklist,
      ['MAX_ALLOWED_SKEW', 'max_allowed_skew'],
      create_default_checklist_value(),
    ),
  );
}

export function get_system_checklist_value(checklist, app_code) {
  return sanitize_checklist_value(
    get_first_available_value(
      checklist,
      ['SYSTEM', 'system'],
      create_default_checklist_value(app_code || 'Not available'),
    ),
    app_code || 'Not available',
  );
}

export function normalize_configuration_checklist(checklist, app_code) {
  return {
    INTERNAL_API_KEY: get_internal_api_key_checklist_value(checklist),
    SSO_SHARED_SECRET: get_sso_secret_checklist_value(checklist),
    MAX_ALLOWED_SKEW: get_max_allowed_skew_checklist_value(checklist),
    SYSTEM: get_system_checklist_value(checklist, app_code),
  };
}

function get_platform_id(platform) {
  return get_first_available_value(platform, PLATFORM_ID_KEYS, null);
}

function get_platform_name(platform) {
  return get_first_available_value(platform, PLATFORM_NAME_KEYS, 'Unnamed Platform');
}

function get_platform_app_code(platform) {
  return get_value(platform, 'app_code', '');
}

function get_platform_environment(platform) {
  return get_value(platform, 'environment', '');
}

function get_platform_status(platform) {
  return get_value(platform, 'status', 'Connected');
}

export function normalize_platform(platform = {}) {
  return {
    id: get_platform_id(platform),
    name: get_platform_name(platform),
    app_code: get_platform_app_code(platform),
    environment: get_platform_environment(platform),
    status: get_platform_status(platform),
  };
}

function get_configuration_id(configuration) {
  return get_first_available_value(configuration, ['id', 'platform_config_id'], null);
}

function get_configuration_platform_id(configuration) {
  return get_first_available_value(configuration, ['platform', 'platform_id'], null);
}

function get_configuration_organization_id(configuration) {
  return get_first_available_value(configuration, ['organization_id'], null);
}

function get_configuration_platform_name(configuration) {
  return get_first_available_value(configuration, ['platform_name', 'name'], 'Unknown Platform');
}

function get_configuration_app_code(configuration) {
  return get_value(configuration, 'app_code', '');
}

function get_configuration_environment(configuration) {
  return get_value(configuration, 'environment', '');
}

function get_configuration_base_url(configuration) {
  return get_value(configuration, 'base_url', '');
}

function get_configuration_launch_url(configuration) {
  return get_value(configuration, 'launch_url', '');
}

function get_configuration_callback_url(configuration) {
  return get_value(configuration, 'callback_url', '');
}

function get_configuration_health_endpoint(configuration) {
  return get_value(configuration, 'health_endpoint', '');
}

function get_configuration_status(configuration) {
  return get_value(configuration, 'status', 'Draft');
}

function get_configuration_signature_rules(configuration) {
  return get_first_available_value(configuration, ['signature_rules'], {});
}

function get_configuration_routing_rules(configuration) {
  return get_first_available_value(configuration, ['routing_rules'], []);
}

function get_configuration_signer_salt(configuration) {
  return get_value(get_configuration_signature_rules(configuration), 'signer_salt', '');
}

function get_configuration_max_allowed_skew(configuration) {
  return get_value(get_configuration_signature_rules(configuration), 'max_allowed_skew', 300);
}

function get_configuration_system(configuration) {
  return get_value(get_configuration_signature_rules(configuration), 'system', '');
}

function get_configuration_github_sync_status(configuration) {
  return get_first_available_value(
    configuration,
    ['deployment_sync_status', 'github_sync_status'],
    'Not Synced',
  );
}

function get_configuration_checklist(configuration) {
  return get_first_available_value(configuration, ['checklist', 'deployment_checklist'], {});
}

export function normalize_configuration(configuration = {}) {
  const app_code = get_configuration_app_code(configuration);
  const platform_id = get_configuration_platform_id(configuration);
  const routing_rules = get_configuration_routing_rules(configuration);

  return {
    id: get_configuration_id(configuration),
    platform: platform_id,
    platform_id,
    platform_name: get_configuration_platform_name(configuration),
    app_code,
    organization_id: get_configuration_organization_id(configuration),
    environment: get_configuration_environment(configuration),
    base_url: get_configuration_base_url(configuration),
    launch_url: get_configuration_launch_url(configuration),
    callback_url: get_configuration_callback_url(configuration),
    health_endpoint: get_configuration_health_endpoint(configuration),
    signer_salt: get_configuration_signer_salt(configuration),
    max_allowed_skew: get_configuration_max_allowed_skew(configuration),
    system: get_configuration_system(configuration),
    routing_rules,
    routing_rules_text: JSON.stringify(routing_rules, null, 2),
    status: get_configuration_status(configuration),
    github_sync_status: get_configuration_github_sync_status(configuration),
    checklist: normalize_configuration_checklist(get_configuration_checklist(configuration), app_code),
  };
}

function get_assignment_id(assignment) {
  return get_first_available_value(assignment, ASSIGNMENT_ID_KEYS, null);
}

function get_assignment_platform_name(assignment) {
  return get_first_available_value(assignment, ASSIGNMENT_PLATFORM_NAME_KEYS, 'Unknown Platform');
}

function get_assignment_principal_name(assignment) {
  return get_first_available_value(assignment, ASSIGNMENT_PRINCIPAL_NAME_KEYS, 'Unassigned');
}

function get_assignment_access_level(assignment) {
  return get_first_available_value(assignment, ASSIGNMENT_ACCESS_LEVEL_KEYS, 'Launch + Configure');
}

function get_assignment_environment(assignment) {
  return get_value(assignment, 'environment', 'Unknown');
}

function get_assignment_status(assignment) {
  return get_value(assignment, 'status', 'Assigned');
}

export function normalize_access_assignment(assignment = {}) {
  return {
    id: get_assignment_id(assignment),
    platform_name: get_assignment_platform_name(assignment),
    principal_name: get_assignment_principal_name(assignment),
    access_level: get_assignment_access_level(assignment),
    environment: get_assignment_environment(assignment),
    status: get_assignment_status(assignment),
  };
}

export function get_safe_redirect_url(redirect_url) {
  try {
    const parsed_url = new URL(redirect_url, window.location.origin);
    const allowed_protocols = ['http:', 'https:'];
    return allowed_protocols.includes(parsed_url.protocol) ? parsed_url.toString() : null;
  } catch {
    return null;
  }
}

export function create_selected_readiness_result(configuration) {
  return {
    ...clone_default_readiness(),
    selected_platform_name: configuration.platform_name,
    environment: configuration.environment,
    health_endpoint_status: configuration.status === 'Ready' ? 'Pending check' : 'Needs review',
    upsert_token_endpoint_status: 'Pending',
    sso_assert_status: 'Pending',
    reachable_status: configuration.status === 'Draft' ? 'Unknown' : 'Pending',
  };
}

export function create_failed_readiness_result(readiness_result) {
  return {
    ...readiness_result,
    ...FAILED_READINESS_RESULT,
  };
}

export function create_completed_readiness_result(configuration, data) {
  return {
    selected_platform_name: configuration.platform_name,
    environment: configuration.environment,
    health_endpoint_status: data.health_endpoint_status || data.health_status || 'Passed',
    upsert_token_endpoint_status: data.upsert_token_endpoint_status || data.token_endpoint_status || 'Passed',
    sso_assert_status: data.sso_assert_status || 'Passed',
    reachable_status: data.reachable_status || data.reachable || 'Reachable',
    last_checked_at: data.last_checked_at || new Date().toLocaleString(),
  };
}

function get_launch_test_result(status) {
  if (status === 'Ready') return 'Passed';
  if (status === 'Needs Review') return 'Awaiting Retry';
  return 'Pending';
}

export function build_launch_test_rows(configurations) {
  return configurations.map((configuration) => ({
    id: configuration.id,
    platform_name: configuration.platform_name,
    app_code: configuration.app_code,
    environment: configuration.environment,
    last_tested_at: configuration.last_tested_at || 'Not tested yet',
    result: get_launch_test_result(configuration.status),
  }));
}

export function build_checklist_entries(checklist) {
  return [
    {
      key: 'INTERNAL_API_KEY',
      label: 'INTERNAL_API_KEY',
      value: Reflect.get(checklist, 'INTERNAL_API_KEY') || create_default_checklist_value(),
    },
    {
      key: 'SSO_SHARED_SECRET',
      label: 'SSO_SHARED_SECRET',
      value: Reflect.get(checklist, 'SSO_SHARED_SECRET') || create_default_checklist_value(),
    },
    {
      key: 'MAX_ALLOWED_SKEW',
      label: 'MAX_ALLOWED_SKEW',
      value: Reflect.get(checklist, 'MAX_ALLOWED_SKEW') || create_default_checklist_value(),
    },
    {
      key: 'SYSTEM',
      label: 'SYSTEM',
      value: Reflect.get(checklist, 'SYSTEM') || create_default_checklist_value(),
    },
  ];
}

export function get_api_error_message(response_data, fallback_message) {
  if (!response_data) return fallback_message;

  return (
    response_data.detail ||
    response_data.message ||
    response_data.error ||
    response_data?.data?.detail ||
    fallback_message
  );
}

export function get_status_badge_class(status) {
  if (SUCCESS_BADGE_STATUSES.includes(status)) return 'bg-success-subtle text-success';
  if (WARNING_BADGE_STATUSES.includes(status)) return 'bg-warning-subtle text-warning-emphasis';
  if (status === 'Draft') return 'bg-secondary-subtle text-secondary';
  return 'bg-light text-dark';
}

export function format_status_label(value) {
  return value || 'Unknown';
}

export function get_display_secret(secret_value = {}) {
  return secret_value.masked_value || '********';
}

export function get_copy_secret(secret_value = {}) {
  return secret_value.masked_value || 'Not available';
}
